/**
 * AnimationController — casca fina sobre AnimationMixer. Resolve o estado LÓGICO
 * (Idle/Run/SwordAttack/…) para o nome de clip REAL do GLB via candidatos do
 * ModelRegistry (substring, case-insensitive), com crossfade e one-shot-com-retorno.
 *
 * §3 Movimento: a animação NUNCA controla lógica de movimento/dano (o gameplay é
 * autoritativo). Preferimos clips in-place. Para "mover + atacar" ao mesmo tempo, há
 * suporte a camada: pernas = locomoção, tronco/braços = ataque/hook — via subconjunto de
 * tracks por bone (`subsetClipByBones`), quando o rig expõe uma cintura identificável.
 *
 * Clips ausentes NÃO quebram nada: `play` de um estado sem clip é no-op (warn só em DEV).
 */
import * as THREE from "three";
import type { AnimState, AnimationMap } from "./ModelRegistry";

function isDev(): boolean {
  try {
    return !!import.meta.env && import.meta.env.DEV;
  } catch {
    return false;
  }
}

/** Nome do bone-alvo de um track ("hand_r.quaternion" → "hand_r"; usa o último ponto). */
function trackBone(trackName: string): string {
  const i = trackName.lastIndexOf(".");
  return i >= 0 ? trackName.slice(0, i) : trackName;
}

/** Novo AnimationClip contendo só as tracks cujos nós satisfazem `keep` (ex.: parte superior). */
export function subsetClipByBones(clip: THREE.AnimationClip, keep: (boneName: string) => boolean): THREE.AnimationClip {
  const tracks = clip.tracks.filter((t) => keep(trackBone(t.name))).map((t) => t.clone());
  return new THREE.AnimationClip(`${clip.name}__subset`, clip.duration, tracks);
}

/** Bone da parte SUPERIOR (tronco/braços/cabeça)? Usado p/ camada aditiva de ataque (§11).
 * Exclui root/quadril/pernas/pés; inclui spine/neck/head/clavicle/ombro/braço/mão/dedos. */
export function isUpperBodyBone(name: string): boolean {
  const n = name.toLowerCase();
  if (/^root$|pelvis|(^|_)hips?($|_)|thigh|calf|shin|upperleg|lowerleg|(^|_)leg($|_)|foot|ball|toe|knee/.test(n)) return false;
  return /spine|chest|torso|neck|head|clavicle|shoulder|upperarm|forearm|lowerarm|(^|_)arm($|_)|hand|wrist|finger|index|middle|pinky|ring|thumb/.test(n);
}

export interface PlayOptions {
  loop?: boolean;
  crossfade?: number;
  timeScale?: number;
}

export class AnimationController {
  private readonly mixer: THREE.AnimationMixer;
  private readonly byName = new Map<string, THREE.AnimationClip>();
  private readonly resolvedCache = new Map<AnimState, THREE.AnimationClip | null>();
  private readonly actions = new Map<string, THREE.AnimationAction>();
  private current: THREE.AnimationAction | null = null;
  private currentState: AnimState | null = null;
  private oneShotAction: THREE.AnimationAction | null = null;
  private oneShotReturn: AnimState | null = null;
  // camada aditiva (ataque/hook/hit só no tronco/braços, somado sobre a locomoção)
  private readonly additiveClips = new Map<AnimState, THREE.AnimationClip | null>();
  private readonly additiveActions = new Map<string, THREE.AnimationAction>();
  private activeAdditive: THREE.AnimationAction | null = null;

  constructor(
    root: THREE.Object3D,
    clips: THREE.AnimationClip[],
    private readonly map: AnimationMap,
  ) {
    this.mixer = new THREE.AnimationMixer(root);
    for (const c of clips) this.byName.set(c.name.toLowerCase(), c);
    this.mixer.addEventListener("finished", (e) => this.onFinished((e as unknown as { action: THREE.AnimationAction }).action));
  }

  /** Resolve o clip real para um estado lógico (candidatos → substring). Cacheado. */
  private resolve(state: AnimState): THREE.AnimationClip | null {
    if (this.resolvedCache.has(state)) return this.resolvedCache.get(state)!;
    const candidates = [...(this.map[state] ?? []), state];
    let found: THREE.AnimationClip | null = null;
    outer: for (const cand of candidates) {
      const needle = cand.toLowerCase();
      for (const [name, clip] of this.byName) {
        if (name.includes(needle)) {
          found = clip;
          break outer;
        }
      }
    }
    this.resolvedCache.set(state, found);
    return found;
  }

  has(state: AnimState): boolean {
    return this.resolve(state) !== null;
  }

  /** Lista os estados lógicos que têm clip real (para relatório/validação). */
  availableStates(states: readonly AnimState[]): AnimState[] {
    return states.filter((s) => this.has(s));
  }

  private action(clip: THREE.AnimationClip): THREE.AnimationAction {
    let a = this.actions.get(clip.uuid);
    if (!a) {
      a = this.mixer.clipAction(clip);
      this.actions.set(clip.uuid, a);
    }
    return a;
  }

  /** Toca um estado em loop (locomoção/idle) com crossfade a partir do atual. */
  play(state: AnimState, opts: PlayOptions = {}): void {
    if (this.oneShotAction && state !== "Death") {
      this.oneShotReturn = state;
      return;
    }
    if (state === this.currentState && this.current) {
      this.current.setEffectiveTimeScale(opts.timeScale ?? 1);
      return;
    }
    if (state === "Death") {
      this.oneShotAction = null;
      this.oneShotReturn = null;
      this.activeAdditive?.stop();
      this.activeAdditive = null;
    }
    const clip = this.resolve(state);
    if (!clip) {
      if (isDev() && !this.warned.has(state)) {
        this.warned.add(state);
        console.warn(`[Anim] sem clip p/ "${state}" — ignorado.`);
      }
      return;
    }
    const next = this.action(clip);
    next.enabled = true;
    next.setLoop(opts.loop === false ? THREE.LoopOnce : THREE.LoopRepeat, opts.loop === false ? 1 : Infinity);
    next.setEffectiveTimeScale(opts.timeScale ?? 1);
    next.setEffectiveWeight(1);
    next.clampWhenFinished = opts.loop === false;
    const fade = opts.crossfade ?? 0.2;
    if (this.current && this.current !== next) {
      next.reset().play();
      this.current.crossFadeTo(next, fade, false);
    } else {
      next.reset().setEffectiveWeight(1).play();
    }
    this.current = next;
    this.currentState = state;
  }

  /** One-shot (ataque/hit/hook): toca uma vez e volta a `returnTo` ao terminar. */
  playOnce(state: AnimState, returnTo: AnimState, crossfade = 0.12): void {
    const clip = this.resolve(state);
    if (!clip) {
      // sem clip de ataque específico: mantém o estado atual (gameplay já aplicou o dano).
      return;
    }
    const a = this.action(clip);
    a.reset();
    a.setEffectiveWeight(1);
    a.setEffectiveTimeScale(1);
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    a.enabled = true;
    if (this.current && this.current !== a) this.current.crossFadeTo(a, crossfade, false);
    a.play();
    this.oneShotAction = a;
    this.oneShotReturn = returnTo;
    this.current = a;
    this.currentState = state;
  }

  /**
   * Ataque/hook/hit como CAMADA ADITIVA só no tronco/braços (§10/§11): a locomoção segue
   * tocando no corpo todo (pernas continuam correndo) e o movimento do ataque é somado por
   * cima. Peso com envelope (sobe/desce) para não dar "pop". NÃO interrompe o movimento.
   */
  playAdditiveOnce(state: AnimState, opts: { timeScale?: number } = {}): void {
    const base = this.resolve(state);
    if (!base) return;
    let add = this.additiveClips.get(state);
    if (add === undefined) {
      const upper = subsetClipByBones(base, isUpperBodyBone);
      add = upper.tracks.length ? THREE.AnimationUtils.makeClipAdditive(upper, 0, this.resolve("Idle") ?? base) : null;
      this.additiveClips.set(state, add);
    }
    if (!add) return;
    let a = this.additiveActions.get(add.uuid);
    if (!a) {
      a = this.mixer.clipAction(add, undefined, THREE.AdditiveAnimationBlendMode);
      this.additiveActions.set(add.uuid, a);
    }
    if (this.activeAdditive && this.activeAdditive !== a) {
      this.activeAdditive.stop();
      this.activeAdditive.setEffectiveWeight(0);
    }
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = false;
    a.timeScale = opts.timeScale ?? 1;
    a.setEffectiveWeight(0);
    a.play();
    this.activeAdditive = a;
  }

  /** Estado do clip aditivo atual (DEV/diagnóstico). */
  get additiveState(): boolean {
    return !!this.activeAdditive?.isRunning();
  }

  private onFinished(action: THREE.AnimationAction): void {
    if (action === this.oneShotAction) {
      const back = this.oneShotReturn ?? "Idle";
      this.oneShotAction = null;
      this.oneShotReturn = null;
      this.currentState = null; // força o crossfade de volta
      this.play(back, { crossfade: 0.15 });
    }
  }

  update(dt: number): void {
    this.mixer.update(dt);
    // envelope de peso da camada aditiva (ramp up no começo, ramp down no fim → sem pop)
    const a = this.activeAdditive;
    if (a) {
      if (!a.isRunning()) {
        a.setEffectiveWeight(0);
        this.activeAdditive = null;
      } else {
        const dur = a.getClip().duration || 1;
        const p = Math.min(1, a.time / dur);
        const w = p < 0.15 ? p / 0.15 : p > 0.8 ? Math.max(0, (1 - p) / 0.2) : 1;
        a.setEffectiveWeight(w);
      }
    }
  }

  dispose(): void {
    this.mixer.stopAllAction();
    this.mixer.uncacheRoot(this.mixer.getRoot());
    this.actions.clear();
    this.additiveActions.clear();
    this.additiveClips.clear();
  }

  private readonly warned = new Set<AnimState>();
}
